import { useLocation } from "@tanstack/react-router"; // HACK:featuresに移管する場合はルーティングに依存する対象としたくないので、tanstack routerへの依存は排除したい
import { useEffect, useState } from "react";

import { HStack, VStack } from "@/components/Layout";

import AddStepperNavigation from "./-components/AddStepperNavigation";
import AddStepperStepTitleCard from "./-components/AddStepperStepTitleCard";
import DockAddStepperAction from "./-components/DockAddStepperAction";
import Processing from "./-components/Processing";
import { IMPORT_CSV_FORM_HASH } from "./-constants/hash";
import ImportData from "./-features/ImportData";
import PreviewFile from "./-features/PreviewFile";
import UploadFile from "./-features/UploadFile";
import type { ExportApiUrl, SearchParams, UseTableRows } from "./types";

type Props = {
  baseLink: string;
  baseTitle: string;
  exportApiUrl: ExportApiUrl;
  exportFilename: string;
  templateMessage: {
    templateTitle: string;
    updateExisting: string;
    completeLinkButton: string;
  };
  completeLinkUrl: string;
  unit: string;
  search: SearchParams;
  navigate: (args: { search: SearchParams; hash: string }) => void;
  useTableRows: UseTableRows;
};

const ImportCsv = ({
  baseLink,
  baseTitle,
  exportApiUrl,
  exportFilename,
  templateMessage,
  unit,
  search,
  navigate,
  useTableRows,
  completeLinkUrl,
}: Props) => {
  const location = useLocation();

  const hash = location.hash
    ? location.hash.replace("#", "")
    : IMPORT_CSV_FORM_HASH.UPLOAD_FILE;

  const [uploadedFileKey, setUploadedFileKey] = useState<string | undefined>(
    undefined,
  );

  const {
    tableRows,
    tableColumnDefs,
    mutateTableRows,
    isError,
    isPending,
    filterError,
  } = useTableRows({
    uploadedFileKey,
  });

  // ページがロードされるときにhashをチェックし、uploadFile以外の場合はuploadFileに戻す
  useEffect(() => {
    if (hash !== IMPORT_CSV_FORM_HASH.UPLOAD_FILE && !uploadedFileKey) {
      navigate({
        search,
        hash: IMPORT_CSV_FORM_HASH.UPLOAD_FILE,
      });
    }
  }, [navigate, hash, uploadedFileKey, search]);

  return (
    <HStack className="h-[calc(100vh-64px)] w-full items-start gap-9 p-10">
      <AddStepperNavigation
        link={{ hash: hash, to: baseLink }}
        title={baseTitle}
        isError={isError}
      />

      <VStack className="h-full min-w-0 flex-1 items-start gap-6 p-0">
        <AddStepperStepTitleCard hash={hash} />

        {isPending && <Processing hash={hash} />}

        {!isPending && hash === IMPORT_CSV_FORM_HASH.UPLOAD_FILE && (
          <UploadFile
            setUploadedFileKey={setUploadedFileKey}
            exportApiUrl={exportApiUrl}
            exportFilename={exportFilename}
            templateMessage={templateMessage}
          />
        )}
        {!isPending && hash === IMPORT_CSV_FORM_HASH.PREVIEW_FILE && (
          <PreviewFile
            hash={hash}
            unit={unit}
            search={search}
            navigate={navigate}
            tableColumnDefs={tableColumnDefs}
            tableRows={tableRows}
            mutateTableRows={mutateTableRows}
            filterError={filterError}
          />
        )}
        {!isPending && hash === IMPORT_CSV_FORM_HASH.IMPORT_DATA && (
          <ImportData
            hash={hash}
            unit={unit}
            search={search}
            navigate={navigate}
            tableColumnDefs={tableColumnDefs}
            tableRows={tableRows}
            mutateTableRows={mutateTableRows}
            filterError={filterError}
            completeLinkButton={templateMessage.completeLinkButton}
            completeLinkUrl={completeLinkUrl}
          />
        )}

        <DockAddStepperAction
          link={{ hash: hash, to: baseLink }}
          isError={isError}
          uploadedFileKey={uploadedFileKey}
          mutateTableRows={mutateTableRows}
        />
      </VStack>
    </HStack>
  );
};

export default ImportCsv;
